/**
 * Note tile — read-only markdown view (S2) plus in-place editing (S5):
 * double-click the body, or the hover-revealed pencil affordance, swaps to a
 * plain textarea; blur commits, Escape cancels. Saves route through the
 * existing store -> repository `updateTile` path (never bypassed). Sanitized
 * rendering mirrors NotebookMarkdownPreview (rehype-sanitize keeps raw HTML
 * inert — the S5/S15 XSS fixtures depend on this staying on).
 */
import { useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { toast } from '@/components/ui/toaster';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useDashboardStore } from '../../store/dashboardStore';
import type { NoteTile } from '../../types/dashboardTypes';

type NoteTileViewProps = {
  dashboardId: string;
  tile: NoteTile;
};

export function NoteTileView({ dashboardId, tile }: NoteTileViewProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(tile.markdown);
  const skipNextBlurRef = useRef(false);
  const { track } = useAnalytics();

  const startEditing = () => {
    setDraft(tile.markdown);
    setEditing(true);
  };

  const commit = async () => {
    setEditing(false);
    if (draft === tile.markdown) return;
    const result = await useDashboardStore
      .getState()
      .updateTile(dashboardId, { ...tile, markdown: draft });
    if (result.ok) track('feature:dashboard_note_edit');
    else toast.error({ title: 'Could not save note', description: result.detail });
  };

  const cancel = () => {
    skipNextBlurRef.current = true;
    setDraft(tile.markdown);
    setEditing(false);
  };

  if (editing) {
    return (
      <textarea
        autoFocus
        className="h-full min-h-0 w-full flex-1 resize-none border-0 bg-transparent px-3 py-2 text-sm outline-none"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (skipNextBlurRef.current) {
            skipNextBlurRef.current = false;
            return;
          }
          void commit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        data-testid="note-tile-editor"
      />
    );
  }

  return (
    <div
      className="group/note relative min-h-0 flex-1 overflow-auto"
      data-testid="note-tile-view"
      onDoubleClick={startEditing}
    >
      <button
        onClick={startEditing}
        className="absolute right-1.5 top-1.5 z-10 rounded-sm p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/note:opacity-100"
        title="Edit note"
        aria-label="Edit note"
        data-testid="note-tile-edit-button"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
      {!tile.markdown.trim() ? (
        <div
          className="text-muted-foreground text-sm italic px-3 py-2"
          data-testid="note-tile-empty"
        >
          Empty note — double-click to edit.
        </div>
      ) : (
        <div
          className="prose prose-sm dark:prose-invert max-w-none px-3 py-2"
          data-testid="note-tile-body"
        >
          <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
            {tile.markdown}
          </Markdown>
        </div>
      )}
    </div>
  );
}
